import { Experiment } from './experimentTypes';

// experiment ids — these are used for convenience in code when testing for experiment matches
export enum ExperimentId {
    disableInviteLimit = 'disableInviteLimit',
    disableAutoShiftLimit = 'disableAutoShiftLimit',
    bypassRecaptchaChecks = 'bypassRecaptchaChecks',
    publicTemplateCreation = 'publicTemplateCreation',
    renderSecureImagesThroughProxy = 'renderSecureImagesThroughProxy',
    renderSecureImagesThroughCF = 'renderSecureImagesThroughCF',
    editNotesOnDoubleClick = 'editNotesOnDoubleClick',
    templateOnboardingV2 = 'templateOnboardingV2',
    documentDiffSyncing = 'documentDiffSyncing',
    table = 'table',
    toolbarV2 = 'toolbarV2',
    tableAnnouncement = 'tableAnnouncement',
    aiAssistant = 'aiAssistant',
    graphDb = 'graphDb',
    graphDbAppInit = 'graphDbAppInit',
    // This will be removed once we have a better solution (remember to archive in the DB!)
    disableQQInvites = 'disableQQInvites',
    // Search
    boardSearch = 'boardSearch',
    // Custom templates weren't originally migrated across to neo4j, so we migrate them lazily as well
    customTemplatesGraphMigration = 'customTemplatesGraphMigration',
    clientLocalCache = 'clientLocalCache',
    // Android hybrid board list
    androidHybridBoardList = 'androidHybridBoardList',
    // Until released, this will activate Canvas on iPhone for non-staff members.
    // Staff members are able to activate this feature locally using local feature toggles.
    iosCanvas = 'iosCanvas',
    showUndoRedoButtonsInWeb = 'showUndoRedoButtonsInWeb',

    // Migration flags
    // NOTE: These are not real experiments, they are just used to track the progress of migrations.
    //   They are added to the user's flags object when their data has been migrated.
    //   This allows the server to check if the user supports graph DB syncing by looking at
    //   the user alone, without performing mongo queries in potentially hot areas of the code.
    graphDbSynced = 'graphDbSynced',
    boardSearchIndexComplete = 'boardSearchIndexComplete',
}

// These experiments will be created in the database if they don't exist
// so that experiments ids can be easily synced between environments.
export const DEFAULT_EXPERIMENTS: Partial<Experiment>[] = [
    {
        _id: ExperimentId.disableInviteLimit,
        title: 'User flag: disable invite limit',
    },
    {
        _id: ExperimentId.disableAutoShiftLimit,
        title: 'User flag: disable auto shift limit',
    },
    {
        _id: ExperimentId.bypassRecaptchaChecks,
        title: 'User flag: disable auto shift limit',
    },
    {
        _id: ExperimentId.publicTemplateCreation,
        title: 'User flag: allow public template creation',
        staffEnabled: true,
    },
    {
        _id: ExperimentId.renderSecureImagesThroughProxy,
        title: 'Image security: render via proxy',
    },
    {
        _id: ExperimentId.renderSecureImagesThroughCF,
        title: 'Image security: render via CloudFront',
    },
    {
        _id: ExperimentId.editNotesOnDoubleClick,
        title: 'Testing: Require two clicks to edit notes',
    },
    {
        _id: ExperimentId.templateOnboardingV2,
        title: 'Testing: New template onboarding',
    },
    {
        _id: ExperimentId.table,
        title: 'Testing: Table cards',
    },
    {
        _id: ExperimentId.tableAnnouncement,
        title: 'Switch on announcement for table cards',
    },
    {
        _id: ExperimentId.aiAssistant,
        title: 'Testing: AI Assistant popup',
    },
    {
        _id: ExperimentId.graphDb,
        title: 'Use graph DB for traversals',
    },
    {
        _id: ExperimentId.graphDbAppInit,
        title: 'Use graph DB for app init',
    },
    {
        _id: ExperimentId.disableQQInvites,
        title: 'User flag: Disable QQ invites',
    },
    {
        _id: ExperimentId.documentDiffSyncing,
        title: 'Document content diff syncing',
    },
    {
        _id: ExperimentId.boardSearch,
        title: 'Use board search',
    },
    {
        _id: ExperimentId.clientLocalCache,
        title: 'Use browser cache for instant app loading',
    },
    {
        _id: ExperimentId.androidHybridBoardList,
        title: 'Android: Use web view to display the board list',
    },
    {
        _id: ExperimentId.iosCanvas,
        title: 'iPhone: Use Canvas view on the "Your Boards" tab',
    },
    {
        _id: ExperimentId.showUndoRedoButtonsInWeb,
        title: 'Show undo/redo buttons in web',
    },
    {
        _id: ExperimentId.toolbarV2,
        title: 'New toolbar structure',
    },
];

// These are the fields from the user model that will be taken into account when
// determining if a user is matched to an experiment.
export const USER_MODEL_EXPERIMENT_PROJECTION = {
    flags: 1,
    role: 1,
};
